/**
 * FormContainer Component
 * Main component for rendering form containers with multiple child forms
 */

import React from 'react';
import './FormContainer.css';
import { FormContainerConfig, DataBindingConfig } from '../types/index';
import { useFormContainer } from '../hooks/useFormContainer';
import { useFormValidation } from '../hooks/useFormValidation';
import { useFormSave } from '../hooks/useFormSave';
import { TabsLayout } from './TabsLayout';
import { WizardLayout } from './WizardLayout';
import { AccordionLayout } from './AccordionLayout';

export interface FormContainerProps {
  config: FormContainerConfig;
  initialData?: Record<string, unknown>;
  onDataChange?: (data: Record<string, unknown>) => void;
  /** Render the content for a child form given its id and binding config */
  renderChildForm?: (formID: string, dataBinding: DataBindingConfig) => React.ReactNode;
  /** Theme for styling: 'dark' or 'light' */
  theme?: 'dark' | 'light';
}

/**
 * FormContainer main component
 * Manages:
 * - Layout selection (tabs, wizard)
 * - Service lifecycle
 * - Tab/step navigation
 * - Validation and save
 */
export const FormContainer: React.FC<FormContainerProps> = ({
  config,
  initialData = {},
  onDataChange,
  renderChildForm,
  theme,
}) => {
  const currentTheme = theme || 'light';
  // Guard: ensure childForms is always an array
  const safeConfig = config.childForms ? config : { ...config, childForms: [] as typeof config.childForms };
  // Main form container state and handlers
  const {
    activeIndex,
    isLoading,
    isSaving,
    errors,
    handleTabChange,
    handleSave,
    handleReset,
    getAllData,
  } = useFormContainer(safeConfig, initialData);

  // Validation management
  const { validateAll, hasErrors } = useFormValidation(safeConfig);

  // Save management
  const { saveAll } = useFormSave(safeConfig);

  if (isLoading) {
    return (
      <div className="af-form-container__loading">
        Loading...
      </div>
    );
  }

  // Get active child form config
  const activeChild = safeConfig.childForms[activeIndex];

  // Prepare handlers for navigation
  // Note: validation is handled inside handleTabChange (useFormContainer hook)
  // so we don't duplicate it here
  const handleTabChangeWithValidation = async (newIndex: number) => {
    await handleTabChange(newIndex);
  };

  const handleWizardNext = async () => {
    if (activeIndex < safeConfig.childForms.length - 1) {
      await handleTabChange(activeIndex + 1);
    }
  };

  const handleWizardPrev = async () => {
    if (activeIndex > 0) {
      await handleTabChange(activeIndex - 1);
    }
  };

  const handleWizardSubmit = async () => {
    await validateAll();
    if (!hasErrors) {
      await handleSave();
    }
  };

  const childFormContent = (
    <div className="af-form-container__child-content">
      {activeChild?.label && !safeConfig.hideFormTitles && (
        <h3 className="af-form-container__child-title">
          {activeChild.label}
        </h3>
      )}
      <div>
        {activeChild?.formID && renderChildForm
          ? renderChildForm(activeChild.formID, activeChild.dataBinding)
          : <p className="af-form-container__child-placeholder">Form {activeChild?.formID} not loaded</p>
        }
      </div>
    </div>
  );

  return (
    <div className="af-form-container">
      {safeConfig.layout === 'tabs-left' || safeConfig.layout === 'tabs-top' ? (
        <TabsLayout
          config={safeConfig}
          activeIndex={activeIndex}
          onTabChange={handleTabChangeWithValidation}
          errors={errors}
          theme={currentTheme}
        >
          {childFormContent}
        </TabsLayout>
      ) : safeConfig.layout === 'wizard' ? (
        <WizardLayout
          config={safeConfig}
          activeIndex={activeIndex}
          onPrev={handleWizardPrev}
          onNext={handleWizardNext}
          onSubmit={handleWizardSubmit}
          canNext={!hasErrors || safeConfig.validationMode !== 'blocked'}
          errors={errors}
          theme={currentTheme}
        >
          {childFormContent}
        </WizardLayout>
      ) : safeConfig.layout === 'accordion' ? (
        <AccordionLayout
          config={safeConfig}
          activeIndex={activeIndex}
          onTabChange={handleTabChangeWithValidation}
          errors={errors}
          theme={currentTheme}
        >
          {childFormContent}
        </AccordionLayout>
      ) : (
        // Default to tabs-top for unknown layouts
        <TabsLayout
          config={safeConfig}
          activeIndex={activeIndex}
          onTabChange={handleTabChangeWithValidation}
          errors={errors}
          theme={currentTheme}
        >
          {childFormContent}
        </TabsLayout>
      )}
    </div>
  );
};

FormContainer.displayName = 'FormContainer';
